export type TermgameOrder = Record<string, any>;

export const PAGE_SIZE = 50;
export const PROFIT_RATE = 0.02;

export const normalizeTermgameOrders = (response: any): TermgameOrder[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.list)) return response.list;
  if (Array.isArray(response?.orders)) return response.orders;
  if (Array.isArray(response?.docs)) return response.docs;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  if (Array.isArray(response?.data?.list)) return response.data.list;
  if (Array.isArray(response?.data?.orders)) return response.data.orders;
  if (Array.isArray(response?.data?.docs)) return response.data.docs;
  if (Array.isArray(response?.data?.results)) return response.data.results;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.data?.items)) return response.data.data.items;
  if (Array.isArray(response?.data?.data?.rows)) return response.data.data.rows;
  if (Array.isArray(response?.data?.data?.list)) return response.data.data.list;
  if (Array.isArray(response?.data?.data?.orders)) return response.data.data.orders;
  if (Array.isArray(response?.data?.data?.docs)) return response.data.data.docs;
  if (Array.isArray(response?.data?.data?.results)) return response.data.data.results;
  return [];
};

export const getOrderCountValue = (response: any) => {
  const candidates = [
    response?.count,
    response?.total,
    response?.totalCount,
    response?.totalItems,
    response?.totalDocs,
    response?.totalResults,
    response?.pagination?.total,
    response?.pagination?.totalItems,
    response?.meta?.total,
    response?.meta?.totalItems,
    response?.data?.count,
    response?.data?.total,
    response?.data?.totalCount,
    response?.data?.totalItems,
    response?.data?.totalDocs,
    response?.data?.totalResults,
    response?.data?.pagination?.total,
    response?.data?.pagination?.totalItems,
    response?.data?.meta?.total,
    response?.data?.meta?.totalItems,
    response?.data?.data?.count,
    response?.data?.data?.total,
    response?.data?.data?.totalCount,
    response?.data?.data?.totalItems,
    response?.data?.data?.totalDocs,
    response?.data?.data?.totalResults,
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === "") {
      continue;
    }

    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }

  return undefined;
};

export const getOrderId = (order: TermgameOrder) =>
  order?._id || order?.id || order?.orderId || order?.donationId || "";

export const getOrderCode = (order: TermgameOrder) =>
  order?.code || order?.ref || order?.reference || order?.transactionNo || "--";

export const getOrderType = (order: TermgameOrder) =>
  order?.type || order?.transactionType || "--";

export const getOrderTypeLabel = (order: TermgameOrder) => {
  const type = String(getOrderType(order)).toUpperCase();

  switch (type) {
    case "DONATION":
      return "Donation";
    case "WITHDRAWAL":
      return "Withdrawal";
    case "ADJUSTMENT":
      return "Adjustment";
    case "TERMGAME_INCOME":
      return "ລາຍໄດ້ຈາກເວັບເຕີມເກມ";
    default:
      return type || "--";
  }
};

export const getOrderTitle = (order: TermgameOrder) =>
  order?.title || order?.name || order?.transactionTitle || "--";

export const getOrderDonorName = (order: TermgameOrder) =>
  order?.donorName || order?.userName || order?.customerName || "--";

export const getOrderMessage = (order: TermgameOrder) =>
  order?.message || order?.description || order?.note || "";

export const getOrderCurrency = (order: TermgameOrder) =>
  order?.currency || order?.currencySymbol || "ກີບ";

export const getOrderBalanceBefore = (order: TermgameOrder) => {
  const value = order?.balanceBefore;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

export const getOrderBalanceAfter = (order: TermgameOrder) => {
  const value = order?.balanceAfter;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

export const getOrderDisplayCount = (order: TermgameOrder) => {
  const value = Number(order?.displayCount ?? 0);
  return Number.isFinite(value) ? value : 0;
};

export const getOrderLastDisplayedAt = (order: TermgameOrder) =>
  order?.lastDisplayedAt || null;

export const getOrderSignedAmount = (order: TermgameOrder) => {
  const amount = getOrderAmount(order);
  const txType = String(getOrderType(order)).toUpperCase();

  if (txType === "WITHDRAWAL") return -Math.abs(amount);
  return amount;
};

export const getGameName = (order: TermgameOrder) =>
  order?.gameName ||
  order?.productAgentName ||
  order?.game?.name ||
  order?.product?.name ||
  (typeof order?.product === "string" ? order.product : "") ||
  order?.orders?.[0]?.product?.name ||
  (typeof order?.orders?.[0]?.product === "string"
    ? order.orders[0].product
    : "") ||
  order?.orders?.[0]?.productAgentName ||
  order?.orders?.[0]?.gameName ||
  "--";

export const getPackageName = (order: TermgameOrder) =>
  order?.packageName ||
  order?.packageGame ||
  order?.productAgentPackageRef?.packageGame ||
  order?.package?.name ||
  (typeof order?.package === "string" ? order.package : "") ||
  order?.orders?.[0]?.package?.name ||
  (typeof order?.orders?.[0]?.package === "string"
    ? order.orders[0].package
    : "") ||
  order?.orders?.[0]?.packageName ||
  order?.orders?.[0]?.packageGame ||
  order?.orders?.[0]?.productAgentPackageRef?.packageGame ||
  "--";

export const getOrderPayload = (order: TermgameOrder) => {
  const payload =
    order?.payload ??
    order?.description ??
    order?.remark ??
    order?.note ??
    order?.playerId ??
    order?.gameId ??
    order?.userId ??
    order?.orders?.[0]?.payload ??
    order?.orders?.[0]?.playerId ??
    order?.orders?.[0]?.gameId ??
    order?.orders?.[0]?.userId;

  if (Array.isArray(payload)) return payload.filter(Boolean).join(", ");
  if (payload === undefined || payload === null || payload === "") return "--";
  return String(payload);
};

export const getOrderAmount = (order: TermgameOrder) => {
  const value =
    order?.transactionAmount ??
    order?.total ??
    order?.totalAmount ??
    order?.amount ??
    order?.price ??
    order?.paymentAmount ??
    order?.orders?.[0]?.total ??
    order?.orders?.[0]?.amount ??
    order?.orders?.[0]?.price ??
    0;

  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

export const getOrderTotalAmountValue = (response: any) => {
  const candidates = [
    response?.totalAmount,
    response?.amountTotal,
    response?.sumAmount,
    response?.sumTotalAmount,
    response?.totalRevenue,
    response?.grossAmount,
    response?.summary?.totalAmount,
    response?.summary?.amountTotal,
    response?.summary?.sumAmount,
    response?.meta?.totalAmount,
    response?.meta?.amountTotal,
    response?.data?.totalAmount,
    response?.data?.amountTotal,
    response?.data?.sumAmount,
    response?.data?.sumTotalAmount,
    response?.data?.totalRevenue,
    response?.data?.grossAmount,
    response?.data?.summary?.totalAmount,
    response?.data?.summary?.amountTotal,
    response?.data?.summary?.sumAmount,
    response?.data?.meta?.totalAmount,
    response?.data?.meta?.amountTotal,
    response?.data?.data?.totalAmount,
    response?.data?.data?.amountTotal,
    response?.data?.data?.sumAmount,
    response?.data?.data?.sumTotalAmount,
    response?.data?.data?.totalRevenue,
    response?.data?.data?.grossAmount,
    response?.data?.data?.summary?.totalAmount,
    response?.data?.data?.summary?.amountTotal,
    response?.data?.data?.summary?.sumAmount,
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === "") {
      continue;
    }

    const value =
      typeof candidate === "string" ? candidate.replace(/,/g, "") : candidate;
    const amount = Number(value);
    if (Number.isFinite(amount)) return amount;
  }

  return undefined;
};

export const getOrderStatus = (order: TermgameOrder) => order?.status || "--";

export const getOrderCreatedAt = (order: TermgameOrder) =>
  order?.createdAt || order?.orderedAt || order?.createAt || order?.updatedAt;

export const getPaginationHasMore = (response: any) => {
  const candidates = [
    response?.pagination?.hasMore,
    response?.hasMore,
    response?.data?.pagination?.hasMore,
    response?.data?.hasMore,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "boolean") return candidate;
  }

  return undefined;
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("lo-LA").format(amount);
};
